import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";

import { useAuth } from "pages/context/AuthContext";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogObjetivoV2DashboardDetalhe from "pages/components/DialogObjetivoV2DashboardDetalhe";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableViewIcon from "@mui/icons-material/TableView";

import * as XLSX from "xlsx-js-style";

function toISODate(value) {
  if (!value) return "";

  return String(value).slice(0, 10);
}

function fmtNumero(value, casas = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function fmtPercentagem(value, casas = 1) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return `${n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;
}

function casasPorMetrica(metrica, tipo = "resultado", modo = "") {
  const m = String(metrica || "")
    .trim()
    .toUpperCase();

  const modoNorm = String(modo || "")
    .trim()
    .toUpperCase();

  if (m === "CLIENTES") {
    return modoNorm === "MEDIA" ? 3 : 0;
  }

  if (m === "QTD") {
    return 0;
  }

  if (m === "EUROS") {
    return 2;
  }

  if (m === "LITROS") {
    return 0;
  }

  return 2;
}

function safeNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function nomeFolhaExcel(nome, fallback = "Folha") {
  const limpo = String(nome || "")
    .replace(/[\\/?*[\]:]/g, "")
    .trim();

  return (limpo || fallback).slice(0, 31);
}

function formatarCabecalhoExcel(ws) {
  if (!ws?.["!ref"]) return;

  const range = XLSX.utils.decode_range(ws["!ref"]);

  for (let col = range.s.c; col <= range.e.c; col += 1) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];

    if (!cell) continue;

    cell.s = {
      ...(cell.s || {}),
      font: {
        ...(cell.s?.font || {}),
        bold: true,
      },
    };
  }
}

function centrarColunasExcel(ws, colunas, totalLinhas) {
  for (let row = 1; row <= totalLinhas + 1; row += 1) {
    colunas.forEach((coluna) => {
      const cell = ws[`${coluna}${row}`];

      if (!cell) return;

      cell.s = {
        ...(cell.s || {}),
        alignment: {
          ...(cell.s?.alignment || {}),
          horizontal: "center",
          vertical: "center",
        },
      };
    });
  }
}

function linhasDetalheExcel(linhas) {
  return (Array.isArray(linhas) ? linhas : []).map((x) => ({
    Cliente: x?.cliente || "",
    Submarca: x?.submarca || "",
    Data: x?.faturadata ? String(x.faturadata).slice(0, 10) : "",
    Fatura: x?.faturanumero || "",
    Material: x?.material || "",
    QTD: safeNum(x?.qtd),
    Euros: safeNum(x?.euros),
    Litros: safeNum(x?.litros),
  }));
}

export default function ObjetivosV2EstadoDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
  onEmptyChange,
}) {
  const { userData } = useAuth();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [carregado, setCarregado] = useState(false);

  const [openDetalhe, setOpenDetalhe] = useState(false);
  const [objetivoDetalhe, setObjetivoDetalhe] = useState(null);

  const carregar = async () => {
    setLoading(true);
    onLoadingChange?.(true);

    try {
      const [data, err] = await handleApi(
        api.post("Comercial/SelObjetivosV2Resumo", {
          apenasVigentes: true,
          idutilizador: userData?.id || 0,
        })
      );

      if (err) {
        setRows([]);
        setCarregado(true);
        onLoadedChange?.(true);
        onEmptyChange?.(true);
        return;
      }

      const arr = Array.isArray(data) ? data : data?.data || [];

      setRows(arr);
      setCarregado(true);

      onLoadedChange?.(true);
      onEmptyChange?.(arr.length === 0);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) {
      return;
    }

    carregar();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken, userData?.id]);

  const columns = useMemo(
    () => [
      {
        Header: "Tipo",
        id: "tipo",
        width: 110,
        Cell: () => "Vendedor",
      },
      {
        Header: "Nome",
        accessor: "objetivo_nome",
        width: 280,
        Cell: ({ value, row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toLowerCase();

          return metrica ? `${value} (${metrica})` : value;
        },
      },
      {
        Header: "%",
        accessor: "atingimento_percentagem",
        width: 170,
        sortType: "numPt",
        Cell: ({ value, row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toUpperCase();

          if (metrica === "SUBMARCAS") {
            return "";
          }
          const valor = Number(value);

          if (!Number.isFinite(valor)) {
            return "";
          }

          // Tal como no V1:
          // o texto pode ultrapassar 100%, mas a barra visual fica limitada a 100%.
          const percentagemVisual = Math.max(0, Math.min(100, valor));

          return (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 150,
              }}
            >
              <MDTypography
                variant="caption"
                sx={{
                  minWidth: 42,
                  textAlign: "right",
                  fontSize: 12,
                  color: "#344767",
                }}
              >
                {valor.toFixed(1)}%
              </MDTypography>

              <MDBox
                sx={{
                  position: "relative",
                  width: 92,
                  height: 9,
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.10)",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: `${percentagemVisual}%`,
                    height: "100%",
                    overflow: "hidden",
                    transition: "width 0.25s ease",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 92,
                      height: "100%",
                      background:
                        "linear-gradient(90deg, #f44336 0%, #ff5722 25%, #ffeb3b 50%, #8bc34a 75%, #4caf50 100%)",
                    }}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          );
        },
      },
      {
        Header: "Objetivo",
        accessor: "valor_objetivo",
        width: 120,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toUpperCase();

          if (metrica === "SUBMARCAS") {
            return "";
          }

          return fmtNumero(
            value,
            casasPorMetrica(row?.original?.metrica, "objetivo", row?.original?.patamares_modo)
          );
        },
      },
      {
        Header: "Real",
        accessor: "resultado",
        width: 120,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toUpperCase();

          if (metrica === "SUBMARCAS") {
            return "";
          }

          return (
            <strong>
              {fmtNumero(
                value,
                casasPorMetrica(row?.original?.metrica, "resultado", row?.original?.patamares_modo)
              )}
            </strong>
          );
        },
      },
      {
        Header: "Dif.",
        id: "diferenca",
        width: 120,
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toUpperCase();

          if (metrica === "SUBMARCAS") {
            return "";
          }

          const objetivo = Number(row?.original?.valor_objetivo ?? 0);
          const real = Number(row?.original?.resultado ?? 0);

          if (!Number.isFinite(objetivo) || !Number.isFinite(real)) {
            return "";
          }

          const diferenca = real - objetivo;

          const cor = diferenca > 0 ? "#4caf50" : diferenca < 0 ? "#d32f2f" : undefined;

          return (
            <span
              style={{
                color: cor,
                fontWeight: 500,
              }}
            >
              {fmtNumero(
                diferenca,
                casasPorMetrica(row?.original?.metrica, "resultado", row?.original?.patamares_modo)
              )}
            </span>
          );
        },
      },
      {
        Header: "Início",
        accessor: "dataini",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Contrapartida",
        accessor: "contrapartida_percentagem",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) => {
          const metrica = String(row?.original?.metrica || "")
            .trim()
            .toUpperCase();

          if (metrica === "SUBMARCAS") {
            return "";
          }

          return value === null || value === undefined ? "" : fmtPercentagem(value, 2);
        },
      },
    ],
    []
  );

  const exportarObjetivoExcel = async (row) => {
    const idObjetivo = Number(row?.id || 0);

    if (idObjetivo <= 0) {
      return;
    }

    try {
      const metricaRow = String(row?.metrica || "")
        .trim()
        .toUpperCase();

      if (metricaRow === "SUBMARCAS") {
        const [detalheSubmarcas, errSubmarcas] = await handleApi(
          api.post("Comercial/SelObjetivoV2SubmarcasMatriz", {
            id: idObjetivo,
            refvendedor: "",
            modoclientes: "todos",
          })
        );

        if (errSubmarcas || !detalheSubmarcas) {
          return;
        }

        const submarcas = Array.isArray(detalheSubmarcas?.submarcas)
          ? detalheSubmarcas.submarcas
          : [];

        const linhas = Array.isArray(detalheSubmarcas?.linhas) ? detalheSubmarcas.linhas : [];

        const dados = linhas.map((linha) => {
          const item = {
            Cliente: linha?.nif
              ? `${linha?.cliente || linha?.refcliente || ""} (${linha.nif})`
              : linha?.cliente || linha?.refcliente || "",
            Vendedor: linha?.vendedor || linha?.refvendedor || "",
            Total: Number(linha?.total || 0),
          };

          submarcas.forEach((sm) => {
            const ref = String(sm?.ref || "").trim();

            if (!ref) return;

            const nome = String(sm?.nome || ref).trim();

            item[nome] = Number(linha?.[`sm_${ref}`] || 0) === 1 ? "Sim" : "Não";
          });

          return item;
        });

        const wb = XLSX.utils.book_new();

        const ws = XLSX.utils.json_to_sheet(dados);

        formatarCabecalhoExcel(ws);

        ws["!cols"] = [
          { wch: 46 },
          { wch: 28 },
          { wch: 10 },
          ...submarcas.map(() => ({ wch: 18 })),
        ];

        XLSX.utils.book_append_sheet(
          wb,
          ws,
          nomeFolhaExcel(row?.objetivo_nome || "Submarcas", "Submarcas")
        );

        const nomeObjetivo =
          String(row?.objetivo_nome || `Objetivo_${idObjetivo}`).trim() || `Objetivo_${idObjetivo}`;

        const nomeLimpo =
          nomeObjetivo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") ||
          `Objetivo_${idObjetivo}`;

        const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

        XLSX.writeFile(wb, `${nomeLimpo}_submarcas_${hoje}.xlsx`);

        return;
      }

      const [detalhe, errDetalhe] = await handleApi(
        api.post("Comercial/SelObjetivoV2DashboardDetalhe", {
          id: idObjetivo,
        })
      );

      if (errDetalhe || !detalhe) {
        return;
      }

      const resumo = [];

      const equipas = Array.isArray(detalhe?.equipas) ? detalhe.equipas : [];

      equipas.forEach((eq) => {
        resumo.push({
          Tipo: "Equipa",
          Vendedor: eq?.nome || eq?.referencia || "",
          Objetivo: safeNum(eq?.objetivo),
          Real: safeNum(eq?.resultado),
          "Dif.": safeNum(eq?.diferenca),
          "%": eq?.atingimento_percentagem == null ? null : safeNum(eq.atingimento_percentagem),
        });
      });

      const vendedores = Array.isArray(detalhe?.vendedores) ? detalhe.vendedores : [];

      vendedores.forEach((v) => {
        resumo.push({
          Tipo: "Individual",
          Vendedor: v?.nome || v?.referencia || "",
          Objetivo: safeNum(v?.objetivo),
          Real: safeNum(v?.resultado),
          "Dif.": safeNum(v?.diferenca),
          "%": v?.atingimento_percentagem == null ? null : safeNum(v.atingimento_percentagem),
        });
      });

      resumo.push({
        Tipo: "Total Geral",
        Vendedor: "",
        Objetivo: safeNum(detalhe?.total?.objetivo),
        Real: safeNum(detalhe?.total?.resultado),
        "Dif.": safeNum(detalhe?.total?.diferenca),
        "%":
          detalhe?.total?.atingimento_percentagem == null
            ? null
            : safeNum(detalhe.total.atingimento_percentagem),
      });

      const wb = XLSX.utils.book_new();

      // ---------------------------------------------------------
      // RESUMO
      // ---------------------------------------------------------
      const wsResumo = XLSX.utils.json_to_sheet(resumo);

      formatarCabecalhoExcel(wsResumo);

      for (let rowExcel = 2; rowExcel <= resumo.length + 1; rowExcel += 1) {
        const cell = wsResumo[`F${rowExcel}`];

        if (cell && typeof cell.v === "number") {
          cell.z = "0.0";
        }
      }

      centrarColunasExcel(wsResumo, ["C", "D", "E", "F"], resumo.length);

      wsResumo["!cols"] = [
        { wch: 16 },
        { wch: 32 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

      // ---------------------------------------------------------
      // EQUIPA
      // ---------------------------------------------------------
      if (equipas.length > 0) {
        const equipa = equipas[0];
        const idEquipa = Number(equipa?.referencia || 0);

        if (idEquipa > 0) {
          const [dadosEquipa, errEquipa] = await handleApi(
            api.post("Comercial/SelObjetivoV2DashboardEquipaFaturas", {
              idobjetivo: idObjetivo,
              idequipa: idEquipa,
            })
          );

          if (!errEquipa && dadosEquipa) {
            const linhasEquipa = (
              Array.isArray(dadosEquipa?.faturas) ? dadosEquipa.faturas : []
            ).map((x) => ({
              Vendedor: x?.vendedor || x?.refvendedor || "",
              Cliente: x?.cliente || "",
              Submarca: x?.submarca || "",
              Data: x?.faturadata ? String(x.faturadata).slice(0, 10) : "",
              Fatura: x?.faturanumero || "",
              Material: x?.material || "",
              QTD: safeNum(x?.qtd),
              Euros: safeNum(x?.euros),
              Litros: safeNum(x?.litros),
            }));

            const wsEquipa = XLSX.utils.json_to_sheet(linhasEquipa, {
              header: [
                "Vendedor",
                "Cliente",
                "Submarca",
                "Data",
                "Fatura",
                "Material",
                "QTD",
                "Euros",
                "Litros",
              ],
            });

            formatarCabecalhoExcel(wsEquipa);

            wsEquipa["!cols"] = [
              { wch: 22 },
              { wch: 34 },
              { wch: 24 },
              { wch: 12 },
              { wch: 16 },
              { wch: 48 },
              { wch: 12 },
              { wch: 14 },
              { wch: 14 },
            ];

            XLSX.utils.book_append_sheet(wb, wsEquipa, "Equipa");
          }
        }
      }

      // ---------------------------------------------------------
      // UMA FOLHA POR VENDEDOR
      // ---------------------------------------------------------
      const nomesUsados = new Set(["RESUMO", "EQUIPA"]);

      const respostas = await Promise.all(
        vendedores.map(async (v) => {
          const refvendedor = String(v?.referencia || "").trim();

          if (!refvendedor) {
            return null;
          }

          const [dados, err] = await handleApi(
            api.post("Comercial/SelObjetivoV2DashboardVendedorFaturas", {
              idobjetivo: idObjetivo,
              refvendedor,
            })
          );

          if (err || !dados) {
            return null;
          }

          return {
            vendedor: v,
            dados,
          };
        })
      );

      respostas.filter(Boolean).forEach((item, index) => {
        const vendedor = item.vendedor;
        const faturas = Array.isArray(item?.dados?.faturas)
          ? [...item.dados.faturas].sort((a, b) => {
              const dataA = String(a?.faturadata || "");
              const dataB = String(b?.faturadata || "");

              if (dataA !== dataB) {
                return dataB.localeCompare(dataA);
              }

              const faturaA = String(a?.faturanumero || "");
              const faturaB = String(b?.faturanumero || "");

              return faturaB.localeCompare(faturaA);
            })
          : [];

        let nomeFolha = nomeFolhaExcel(
          vendedor?.nome || vendedor?.referencia,
          `Vendedor ${index + 1}`
        );

        const nomeBase = nomeFolha;
        let contador = 2;

        while (nomesUsados.has(nomeFolha.toUpperCase())) {
          const sufixo = ` ${contador}`;

          nomeFolha = `${nomeBase.slice(0, 31 - sufixo.length)}${sufixo}`;

          contador += 1;
        }

        nomesUsados.add(nomeFolha.toUpperCase());

        const ws = XLSX.utils.json_to_sheet(linhasDetalheExcel(faturas), {
          header: ["Cliente", "Submarca", "Data", "Fatura", "Material", "QTD", "Euros", "Litros"],
        });

        formatarCabecalhoExcel(ws);

        ws["!cols"] = [
          { wch: 34 },
          { wch: 24 },
          { wch: 12 },
          { wch: 16 },
          { wch: 48 },
          { wch: 12 },
          { wch: 14 },
          { wch: 14 },
        ];

        XLSX.utils.book_append_sheet(wb, ws, nomeFolha);
      });

      const nomeObjetivo =
        String(detalhe?.objetivo?.nome || row?.objetivo_nome || `Objetivo_${idObjetivo}`).trim() ||
        `Objetivo_${idObjetivo}`;

      const nomeLimpo =
        nomeObjetivo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") || `Objetivo_${idObjetivo}`;

      const metrica = String(detalhe?.objetivo?.metrica || row?.metrica || "")
        .trim()
        .toLowerCase();

      const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

      XLSX.writeFile(wb, `${nomeLimpo}${metrica ? `_${metrica}` : ""}_${hoje}.xlsx`);
    } catch (err) {
      console.log("Falha ao exportar Objetivo V2 para Excel", err);
    }
  };

  const rowActions = useMemo(
    () => [
      {
        id: "detalhe",
        tooltip: "Ver detalhe",
        icon: <InfoOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: (row) => {
          setObjetivoDetalhe(row);
          setOpenDetalhe(true);
        },
      },
      {
        id: "excel",
        tooltip: "Exportar objetivo para Excel",
        icon: <TableViewIcon fontSize="small" />,
        color: "success",
        onClick: (row) => exportarObjetivoExcel(row),
      },
    ],
    []
  );

  const exportarObjetivosExcel = async (grupo) => {
    const linhas = Array.isArray(grupo?.rows) ? grupo.rows : [];

    if (linhas.length === 0) {
      return;
    }

    const todasSubmarcas =
      linhas.length > 0 &&
      linhas.every(
        (r) =>
          String(r?.metrica || "")
            .trim()
            .toUpperCase() === "SUBMARCAS"
      );

    if (todasSubmarcas) {
      try {
        const wb = XLSX.utils.book_new();
        const nomesUsados = new Set();

        for (let index = 0; index < linhas.length; index += 1) {
          const row = linhas[index];
          const idObjetivo = Number(row?.id || 0);

          if (idObjetivo <= 0) {
            continue;
          }

          const [detalhe, err] = await handleApi(
            api.post("Comercial/SelObjetivoV2SubmarcasMatriz", {
              id: idObjetivo,
              refvendedor: "",
              modoclientes: "todos",
            })
          );

          if (err || !detalhe) {
            continue;
          }

          const submarcas = Array.isArray(detalhe?.submarcas) ? detalhe.submarcas : [];

          const linhasObjetivo = Array.isArray(detalhe?.linhas) ? detalhe.linhas : [];

          const dados = linhasObjetivo.map((linha) => {
            const item = {
              Cliente: linha?.nif
                ? `${linha?.cliente || linha?.refcliente || ""} (${linha.nif})`
                : linha?.cliente || linha?.refcliente || "",
              Vendedor: linha?.vendedor || linha?.refvendedor || "",
              Total: Number(linha?.total || 0),
            };

            submarcas.forEach((sm) => {
              const ref = String(sm?.ref || "").trim();

              if (!ref) return;

              const nome = String(sm?.nome || ref).trim();

              item[nome] = Number(linha?.[`sm_${ref}`] || 0) === 1 ? "Sim" : "Não";
            });

            return item;
          });

          const ws = XLSX.utils.json_to_sheet(dados);

          formatarCabecalhoExcel(ws);

          ws["!cols"] = [
            { wch: 46 },
            { wch: 28 },
            { wch: 10 },
            ...submarcas.map(() => ({ wch: 18 })),
          ];

          let nomeFolha = nomeFolhaExcel(row?.objetivo_nome, `Objetivo ${index + 1}`);

          const nomeBase = nomeFolha;
          let contador = 2;

          while (nomesUsados.has(nomeFolha.toUpperCase())) {
            const sufixo = ` ${contador}`;

            nomeFolha = `${nomeBase.slice(0, 31 - sufixo.length)}${sufixo}`;

            contador += 1;
          }

          nomesUsados.add(nomeFolha.toUpperCase());

          XLSX.utils.book_append_sheet(wb, ws, nomeFolha);
        }

        if (wb.SheetNames.length === 0) {
          return;
        }

        const nomeGrupo = String(grupo?.nome || "Objetivos").trim() || "Objetivos";

        const nomeFicheiro =
          nomeGrupo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") || "Objetivos";

        const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

        XLSX.writeFile(wb, `${nomeFicheiro}_${hoje}.xlsx`);

        return;
      } catch (err) {
        console.log("Falha ao exportar grupo SUBMARCAS para Excel", err);

        return;
      }
    }

    const dados = linhas.map((r) => {
      const objetivo = Number(r?.valor_objetivo ?? 0);
      const real = Number(r?.resultado ?? 0);

      const objetivoSeguro = Number.isFinite(objetivo) ? objetivo : 0;

      const realSeguro = Number.isFinite(real) ? real : 0;

      const metrica = String(r?.metrica || "")
        .trim()
        .toLowerCase();

      return {
        Tipo: "Vendedor",
        Nome: metrica ? `${r?.objetivo_nome || ""} (${metrica})` : r?.objetivo_nome || "",
        "%": Number(r?.atingimento_percentagem ?? 0),
        Objetivo: objetivoSeguro,
        Real: realSeguro,
        "Dif.": realSeguro - objetivoSeguro,
        Início: toISODate(r?.dataini),
        Fim: toISODate(r?.datafim),
      };
    });

    const ws = XLSX.utils.json_to_sheet(dados);

    ws["!cols"] = [
      { wch: 14 },
      { wch: 45 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();

    const nomeGrupo = String(grupo?.nome || "Objetivos").trim() || "Objetivos";

    const nomeFolha = nomeGrupo.replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Objetivos";

    XLSX.utils.book_append_sheet(wb, ws, nomeFolha);

    const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

    const nomeFicheiro = nomeGrupo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") || "Objetivos";

    XLSX.writeFile(wb, `${nomeFicheiro}_${hoje}.xlsx`);
  };

  const grupos = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const idGrupo = row?.idobjetivogrupo ?? 0;

      const nomeGrupo = String(row?.grupo_nome || "Sem grupo").trim() || "Sem grupo";

      if (!map.has(idGrupo)) {
        map.set(idGrupo, {
          id: idGrupo,
          nome: nomeGrupo,
          rows: [],
          idsObjetivos: new Set(),
        });
      }

      const grupo = map.get(idGrupo);

      grupo.rows.push(row);

      if (row?.id != null) {
        grupo.idsObjetivos.add(String(row.id));
      }
    });

    return Array.from(map.values())
      .map((grupo) => ({
        id: grupo.id,
        nome: grupo.nome,
        rows: grupo.rows,
        totalObjetivos: grupo.idsObjetivos.size,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-PT"));
  }, [rows]);

  return (
    <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
      {loading && carregado ? (
        <MDBox py={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </MDBox>
      ) : null}

      {carregado && grupos.length > 0 && (
        <MDBox
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {grupos.map((grupo) => {
            const textoQuantidade =
              grupo.totalObjetivos === 1 ? "1 objetivo" : `${grupo.totalObjetivos} objetivos`;

            return (
              <DashboardLazyAccordion
                key={`objetivos-v2-grupo-${grupo.id}`}
                title={`${grupo.nome} (${textoQuantidade})`}
                headerContent={
                  <Tooltip title="Exportar este grupo para Excel" arrow placement="top">
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={`Exportar ${grupo.nome} para Excel`}
                      sx={{
                        width: 40,
                        height: 40,
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        exportarObjetivosExcel(grupo);
                      }}
                    >
                      <TableViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
                loaded
                showRefreshButton={false}
              >
                <ProjectTable
                  tableKey={`dashboard-objetivos-v2-${grupo.id}`}
                  version="v1"
                  columns={
                    String(grupo?.nome || "")
                      .trim()
                      .toUpperCase() === "PROPAGAÇÃO SUBMARCAS"
                      ? columns.filter(
                          (col) =>
                            ![
                              "atingimento_percentagem",
                              "valor_objetivo",
                              "resultado",
                              "diferenca",
                              "contrapartida_percentagem",
                            ].includes(col.accessor || col.id)
                        )
                      : columns
                  }
                  data={grupo.rows}
                  rowActions={rowActions}
                  actionsPosition="left"
                  emptyMessage="Sem objetivos para apresentar."
                  minWidth={1000}
                  maxHeight={360}
                  showKeepFilters
                />
              </DashboardLazyAccordion>
            );
          })}
        </MDBox>
      )}

      {carregado && grupos.length === 0 && !loading && (
        <MDBox py={2}>
          <MDTypography
            variant="button"
            color="text"
            sx={{
              display: "block",
              textAlign: "center",
              fontWeight: "regular",
            }}
          >
            Não foram encontrados objetivos v2 para apresentar.
          </MDTypography>
        </MDBox>
      )}

      <DialogObjetivoV2DashboardDetalhe
        open={openDetalhe}
        onClose={() => {
          setOpenDetalhe(false);
          setObjetivoDetalhe(null);
        }}
        objetivo={objetivoDetalhe}
      />
    </MDBox>
  );
}
